
import { RetailerLocation } from '@andromeda/shopping-cart-data-access-store';
import {
    AuthOnBehalfOfConfig,
    AuthReference,
    AuthService,
    AuthStatus,
    CartItem,
    createCart,
    getKrogerAuthReference,
    clearToken,
    Error400Response,
    fetchAccessToken,
    getCurrentAccessToken,
    getReferenceId,
    Offer,
    Token,
} from '@andromeda/polaris-data-access-products';
import IProductSlot from '../../models/ProductSlot';


export default class KrogerCheckout {
    private isDefault!: boolean;

    private selectedRetailer!: RetailerLocation;

    private selectedProducts!: IProductSlot<Offer>[];

    private authConfig!: AuthOnBehalfOfConfig;

    private authReference: AuthReference | null = null;

    private token: Token | null = null;

    private chainName: string = '';

    private authInterval: number = -1;

    private authTries: number = 0;

    public loading = false;

    private onCartCreated: (cartUrl: string) => void;

    private onCartError: () => void;


    constructor(isDefault: boolean, retailer: RetailerLocation, products: IProductSlot<Offer>[], authConfig: AuthOnBehalfOfConfig, onCartCreated: (cartUrl: string) => void, onCartError: () => void) {
        this.isDefault = isDefault;
        this.selectedRetailer = retailer;
        this.selectedProducts = products;
        this.authConfig = authConfig;
        this.chainName = retailer.chain.replace(/\s/g, '') || '';
        this.onCartCreated = onCartCreated;
        this.onCartError = onCartError;
    }

    get cartUrl() {
        if (!this.isDefault) {
            return (this.selectedRetailer) ? `https://www.${this.chainName}.com/cart` : '';
        }
        return 'https://www.kroger.com';
    }

    authorizeUser() {
        const referenceId = getReferenceId();
        this.authReference = getKrogerAuthReference(referenceId, this.authConfig);
        window.open(this.authReference.uri, 'Kroger Login', 'resizeable,scrollbars,status');
        // @ts-ignore
        this.authInterval = setInterval(this.onAuthWindowClosed, 1000);
        return this.authReference;
    }

    // Use () => to keep the `this` scope
    private onAuthWindowClosed = async () => {
        if (!this.authReference) return;
        const response = await fetchAccessToken(AuthService.KROGER, this.authReference.referenceId);

        // We have a token
        if (response.status === AuthStatus.SUCCESS) {
            clearInterval(this.authInterval);
            this.token = response as unknown as Token;
            this.addItemsToCart(this.token);
            return;
        }

        // Still Waiting
        if (response.status === AuthStatus.WAITING) {
            console.log('[KROGER AUTH] 404 AuthResponse');
            this.authTries += 1;

            // Give them 10 minutes then call it.
            if (this.authTries > 600) {
                this.loading = false;
                clearInterval(this.authInterval);
            }
        }

        if (response.status === AuthStatus.ERROR) {
            console.log('[KROGER AUTH] User cancelled auth request');
            this.loading = false;
            clearInterval(this.authInterval);
        }
    }

    async addItemsToCart(token: Token): Promise<void> {
        const items = this.cartItems;
        const kroResponse = await createCart(items, token);
        const [cart, err] = kroResponse;
        if (err) {
            const err400 = err as Error400Response;
            if (err400 && err400.status && err400.status === 400) {
                console.error('[KROGER CART] Bad Request');
                this.onCartError();
            } else {
                // Handle Refresh Token
                clearToken(AuthService.KROGER);
                await this.handleClick();
            }
        } else if (!cart) {
            // Handle missing cart
            this.onCartError();
        } else {
            this.onCartCreated(this.cartUrl);
        }
    }

    get cartItems(): CartItem[] {
        return this.selectedProducts.filter(p => p.id !== '' && p.id !== '000000000000')
            .map(p => ({
                upc: p.id,
                specialInstructions: '',
                allowSubstitues: false,
                quantity: 1,
            } as CartItem));
    }

    async handleClick(): Promise<void> {
        this.loading = true;
        // Get current access token
        if (this.isDefault) {
            window.open(this.cartUrl);
        }
        try {
            const token = getCurrentAccessToken(AuthService.KROGER);
            if (!token.accessToken.length) {
                this.authReference = this.authorizeUser();
            } else {
                this.token = token;
                this.addItemsToCart(token);
            }
        } catch (err) {
            console.error('[KROGER CART]', err);
            this.loading = false;
        }
    }
}
